import { ApplicantAutomationState } from './applicant-automation-selectors';
import { FAILURE, STARTING, SUCCESS } from '../action/util/action-name-creators';
import {
  Action,
  setStateDirtyByParam,
  updateResourceByParam,
} from '../reducers/utils/state-manager';

import { APPLICANT_AUTOMATION_DISPLAY_CONDITION_TYPES } from '../automations/utils/applicant-automation-communication-types';
import { convertArrayToMap } from '../util/convert-array-to-map';
import { WEBHOOK_EVENT_SUBSCRIPTION_SUBSCRIBER_TYPE } from '../webhook-event-subscription/utils/webhook-event-subscription-utils';

const ApplicantAutomationInitialState = {
  applicantAutomationsById: {},
  applicantAutomationsStateByJobId: {},
  applicantAutomationsStateByOrgId: {},
};

const getKeyAndValueFromParams = (params: { orgId: number; jobId?: number }) => {
  if (params.jobId) {
    return { key: 'jobId', keyValue: params.jobId.toString() };
  }
  return { key: 'orgId', keyValue: params.orgId.toString() };
};

export default (
  state: ApplicantAutomationState = ApplicantAutomationInitialState,
  action: Action,
) => {
  switch (action.type) {
    case STARTING('LOAD_APPLICANT_AUTOMATIONS'): {
      const { key, keyValue } = getKeyAndValueFromParams(action.payload.args);
      return updateResourceByParam(state, 'applicantAutomations', key, keyValue, true);
    }
    case SUCCESS('LOAD_APPLICANT_AUTOMATIONS'): {
      const { data } = action.payload;
      const { key, keyValue } = getKeyAndValueFromParams(action.payload.args);
      return updateResourceByParam(
        state,
        'applicantAutomations',
        key,
        keyValue,
        false,
        convertArrayToMap(data, (c: ApplicantAutomation) =>
          String(c.applicantTriggeredCommunicationId),
        ),
      );
    }
    case FAILURE('LOAD_APPLICANT_AUTOMATIONS'): {
      const { key, keyValue } = getKeyAndValueFromParams(action.payload.args);
      return updateResourceByParam(state, 'applicantAutomations', key, keyValue, false);
    }
    case SUCCESS('CREATE_APPLICANT_AUTOMATION'): {
      // data type is {applicantAutomations: ApplicantAutomation[]}
      const { applicantAutomations } = action.payload.data;
      let newState = { ...state };

      applicantAutomations.forEach((automation: ApplicantAutomation) => {
        newState = {
          ...newState,
          applicantAutomationsById: {
            ...newState.applicantAutomationsById,
            [automation.applicantTriggeredCommunicationId]: automation,
          },
        };
      });

      return newState;
    }
    case SUCCESS('UPDATE_APPLICANT_AUTOMATION'): {
      const { applicantAutomation, deletedIds } = action.payload.data;

      // Replace updated automation in state
      const newState = {
        ...state,
        applicantAutomationsById: {
          ...state.applicantAutomationsById,
          [applicantAutomation.applicantTriggeredCommunicationId]: applicantAutomation,
        },
      };

      // Remove any deleted items
      (deletedIds || []).forEach((deletedId: number) => {
        delete newState.applicantAutomationsById[deletedId];
      });

      return newState;
    }
    case SUCCESS('DELETE_APPLICANT_AUTOMATION'):
    case SUCCESS('REMOVE_APPLICANT_AUTOMATION_OVERRIDES'): {
      const { data: deletedAutomationIds } = action.payload;

      const newAutomationState = {
        ...state.applicantAutomationsById,
      };

      deletedAutomationIds.forEach((automationId: number) =>
        newAutomationState[automationId] ? delete newAutomationState[automationId] : null,
      );

      return {
        ...state,
        applicantAutomationsById: newAutomationState,
      };
    }
    case SUCCESS('ADD_JOB'):
    case SUCCESS('UPDATE_WEBHOOK_EVENT_SUBSCRIPTION'):
    case SUCCESS('CREATE_WEBHOOK_EVENT_SUBSCRIPTION'): {
      const { orgId } = action.payload.args;
      return setStateDirtyByParam(state, 'applicantAutomations', 'orgId', orgId);
    }

    case SUCCESS('DELETE_WEBHOOK_EVENT_SUBSCRIPTION'): {
      const { subscriberType } = action.payload.args;
      if (subscriberType === WEBHOOK_EVENT_SUBSCRIPTION_SUBSCRIBER_TYPE.INOVA) {
        const newAutomationState = {
          ...state.applicantAutomationsById,
        };

        Object.values(newAutomationState).forEach((automation: ApplicantAutomation) =>
          automation.displayCondition ===
          APPLICANT_AUTOMATION_DISPLAY_CONDITION_TYPES.INOVA_SUBSCRIPTION
            ? delete newAutomationState[automation.applicantTriggeredCommunicationId]
            : null,
        );

        return {
          ...state,
          applicantAutomationsById: newAutomationState,
        };
      }

      return state;
    }
    default:
      return state;
  }
};
